import { Injectable } from '@angular/core';
import { BusyService, EntityTypeLike, getEntityType, LookupTypesCache } from 'app/core/services';
import { Entity, MetadataStore, NavigationProperty, Validator } from 'breeze-client';
import { AuthMetadata } from './auth-entities/metadata';
import { AuthRegistrationHelper } from './auth-entities/registration-helper';
import { Admin, AdminType, User, UserType } from './entities/entity-model';
import { InsolMetadata } from './entities/metadata';
import { InsolRegistrationHelper } from './entities/registration-helper';
import { PortalMetadata } from './portal-entities/metadata';
import { PortalRegistrationHelper } from './portal-entities/registration-helper';
import { Bill, TimeCategory, TimeEntry, UserTypeRate, UserUserType } from './time-entities/entity-model';
import { TimeMetadata } from './time-entities/metadata';
import { TimeRegistrationHelper } from './time-entities/registration-helper';
import { uniqueParentAndDescriptionValidator } from './validation/breeze-validators';

@Injectable({ providedIn: 'root' })
export class MetadataProvider {
  readonly metadataStore: MetadataStore;

  constructor() {
    this.metadataStore = new MetadataStore();

    this.metadataStore.importMetadata(InsolMetadata.value);
    InsolRegistrationHelper.register(this.metadataStore);

    this.metadataStore.importMetadata(AuthMetadata.value);
    AuthRegistrationHelper.register(this.metadataStore);

    this.metadataStore.importMetadata(TimeMetadata.value);
    TimeRegistrationHelper.register(this.metadataStore);

    this.metadataStore.importMetadata(PortalMetadata.value);
    PortalRegistrationHelper.register(this.metadataStore);

    this.patchMetadata(this.metadataStore);
  }

  private patchMetadata(metadataStore: MetadataStore): void {
    // Add navigation properties from TimeEntry to related entities
    // The properties are defined manually on the TimeEntry TypeScript class
    this.addScalarNavigation(metadataStore, Bill, Admin, 'job', 'adminId');
    this.addScalarNavigation(metadataStore, TimeEntry, Admin, 'job', 'adminId');
    this.addScalarNavigation(metadataStore, TimeEntry, AdminType, 'jobType', 'adminTypeId');
    this.addScalarNavigation(metadataStore, TimeEntry, User, 'user', 'userId');
    this.addScalarNavigation(metadataStore, TimeEntry, UserType, 'userType', 'userTypeId');
    this.addScalarNavigation(metadataStore, UserTypeRate, UserType, 'userType', 'userTypeId');
    this.addScalarNavigation(metadataStore, UserUserType, UserType, 'userType', 'userTypeId');
    this.addScalarNavigation(metadataStore, UserUserType, User, 'user', 'userId');

    const timeCategory = getEntityType(TimeCategory, metadataStore);
    const timeCategoryDescription = timeCategory.getProperty('description');
    timeCategoryDescription.validators.push(Validator.required());
    timeCategoryDescription.validators.push(uniqueParentAndDescriptionValidator);
  }

  /** Add scalar navigation propName from entityType to fkEntity
   * @param source - entity on which the property is added
   * @param related - entity that the navigation property points to
   * @param propName - name of the new navigation property
   * @param fkName - name of existing foreign key on entityType
   */
  private addScalarNavigation<T extends Entity, R extends Entity>(
    metadataStore: MetadataStore,
    source: EntityTypeLike<T>,
    related: EntityTypeLike<R>,
    propName: string,
    fkName: string): void
  {
    const entityType = getEntityType(source, metadataStore);
    const fkEntityType = getEntityType(related, metadataStore);

    const navprop = new NavigationProperty({
      name: propName,
      entityTypeName: fkEntityType.name,
      isScalar: true,
      associationName: entityType.shortName + '_' + fkName,
      foreignKeyNames: [fkName],
    });
    entityType.addProperty(navprop);
  }

}

@Injectable({ providedIn: 'root' })
export class GlobalLookupTypesCache extends LookupTypesCache {
  constructor(metadataProvider: MetadataProvider, busyService: BusyService) {
    super(metadataProvider.metadataStore, 'System', 'LookupTypes', busyService);
  }
}
